import axios from 'axios';
import fileDownload from 'js-file-download';

import { ReportFilters } from '../interfaces/ReportFilters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export type ModoRelatorio = 'ATENDIMENTO' | 'ESTEIRAS' | 'FEEDBACK';
export type FormatoRelatorio = 'CSV' | 'PDF';

export interface ColunaDoRelatorio {
  id: string;
  titulo: string;
  secao: string;
}

export interface ModoDoRelatorio {
  id: ModoRelatorio;
  titulo: string;
  /** O que UMA LINHA representa — exibido na tela, para ninguém somar o que não se soma. */
  granularidade: string;
  descricao: string;
  padrao: string[];
  colunas: ColunaDoRelatorio[];
}

export interface OpcoesDoRelatorio {
  modoPadrao: ModoRelatorio;
  maxColunasPdf: number;
  modos: ModoDoRelatorio[];
}

/**
 * O catálogo de modos e colunas (at-107).
 *
 * 🔴 Fonte única: o cardápio do modal vem daqui, nunca de uma lista escrita no frontend. Antes da
 * at-107 as colunas eram declaradas nos dois repositórios, e coluna que só existia aqui virava
 * célula vazia no arquivo — sem erro em lugar nenhum.
 *
 * ⚠️ Sem catch: quem trata a falha é a tela, que precisa BLOQUEAR a geração enquanto não souber
 * quais colunas existem (regra da at-60).
 */
export async function buscarOpcoesDoRelatorio(): Promise<OpcoesDoRelatorio> {
  const resposta = await axios.get(`${BACKEND_URL}/report/cars/opcoes`, {
    withCredentials: true,
  });
  return resposta.data;
}

/** Lê o nome que o servidor mandou; sem ele, o PDF sairia com extensão de CSV. */
function nomeDoArquivo(contentDisposition: unknown, formato: FormatoRelatorio): string {
  const cabecalho = typeof contentDisposition === 'string' ? contentDisposition : '';
  const casado = cabecalho.match(/filename="?([^"]+)"?/i);
  if (casado?.[1]) return casado[1];

  const marca = new Date().toISOString().replace(/[-:.]/g, '').slice(0, 15);
  return `relatorio_veiculos_${marca}.${formato.toLowerCase()}`;
}

export async function gerarRelatorioDeVeiculos(opcoes: {
  campos: string[];
  modo: ModoRelatorio;
  formato: FormatoRelatorio;
  filtros?: ReportFilters;
  inicio?: Date;
  fim?: Date;
}): Promise<void> {
  const parametros = new URLSearchParams();
  parametros.append('fields', opcoes.campos.join(','));
  parametros.append('modo', opcoes.modo);
  parametros.append('formato', opcoes.formato);

  const filtros = (opcoes.filtros ?? {}) as Record<string, unknown>;
  Object.keys(filtros).forEach((chave) => {
    const valor = filtros[chave];
    if (valor !== undefined && valor !== null && `${valor}`.trim() !== '') {
      parametros.append(chave, `${valor}`);
    }
  });

  if (opcoes.inicio) parametros.append('startDate', opcoes.inicio.toISOString().split('T')[0]);
  if (opcoes.fim) parametros.append('endDate', opcoes.fim.toISOString().split('T')[0]);

  const resposta = await axios.get(`${BACKEND_URL}/report/cars?${parametros.toString()}`, {
    responseType: 'blob',
    withCredentials: true,
  });

  fileDownload(
    resposta.data,
    nomeDoArquivo(resposta.headers?.['content-disposition'], opcoes.formato),
  );
}

/**
 * ⚠️ O erro do backend vem como BLOB (a resposta é `responseType: 'blob'`), então a mensagem de
 * "escolheu colunas demais para o PDF" chegaria à tela como `[object Blob]`. Este helper devolve
 * o texto real para o `notify` — sem ele, o usuário leria um erro genérico justamente no caso em
 * que o servidor explica o que fazer.
 */
export async function mensagemDeErroDoRelatorio(erro: unknown): Promise<string | null> {
  if (!axios.isAxiosError(erro) || !erro.response?.data) return null;

  const dados = erro.response.data as unknown;

  if (typeof Blob !== 'undefined' && dados instanceof Blob) {
    const texto = await textoDoBlob(dados);
    return texto ? extrairMensagem(texto) : null;
  }

  if (typeof dados === 'string') return extrairMensagem(dados);

  return (dados as { message?: string })?.message ?? null;
}

/**
 * ⚠️ `Blob.text()` não existe em todo ambiente (o jsdom 16.7, que o react-scripts 5 trava, não o
 * implementa), então o `FileReader` é o caminho que funciona em todos. Sem o fallback, o
 * tratamento parece correto e falha justamente onde é testado.
 */
async function textoDoBlob(blob: Blob): Promise<string | null> {
  if (typeof blob.text === 'function') {
    try {
      return await blob.text();
    } catch {
      return null;
    }
  }

  if (typeof FileReader === 'undefined') return null;

  return new Promise((resolver) => {
    const leitor = new FileReader();
    leitor.onload = () => resolver(typeof leitor.result === 'string' ? leitor.result : null);
    leitor.onerror = () => resolver(null);
    leitor.readAsText(blob);
  });
}

function extrairMensagem(texto: string): string | null {
  try {
    return JSON.parse(texto)?.message ?? null;
  } catch {
    // Corpo que não é JSON: o chamador cai na mensagem própria dele.
    return null;
  }
}
